import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { hasPermission } from '../../utils/roleAccess';
import routePaths from '../../router/route-paths';
import { usePermissions } from '../../hooks/usePermissions';
import { getAccessToken } from '../../utils/auth-token-utils/cookie';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { userRole, user, mustChangePassword } = usePermissions();
  const location = useLocation();
  const token = getAccessToken();

  // Auth is determined by the access token, NOT by the async-loaded Redux user.
  // Gating on `user` here creates an infinite redirect loop on refresh:
  // /dashboard -> (user null) -> "/" -> (AuthenticatedRoutes Navigate) -> /dashboard -> ...
  // which React eventually kills as "Maximum update depth exceeded", unmounting the app.
  if (!token) {
    return <Navigate to={routePaths.HOME} replace />;
  }

  // User profile hasn't loaded yet — render children and let role/permission
  // checks re-run once `userDetails` arrives in Redux.
  if (!user) {
    return children;
  }

  // An account on a temporary password (issued by a full admin) must set its
  // own password before it can use anything else.
  if (mustChangePassword && location.pathname !== routePaths.CHANGE_PASSWORD) {
    return <Navigate to={routePaths.CHANGE_PASSWORD} replace />;
  }

  // If a specific role is required, check if user has that role
  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to={routePaths.DASHBOARD} replace />;
  }

  // Check if user has permission to access the current route
  if (!hasPermission(user, location.pathname)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          textAlign: 'center',
          padding: '2rem'
        }}
      >
        <Typography variant="h4" sx={{ marginBottom: '1rem', color: '#142C5C' }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: '2rem', color: '#666' }}>
          You don't have permission to access this page. Please contact your administrator.
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.history.back()}
          sx={{
            background: '#142C5C',
            '&:hover': { background: '#142C5C' }
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;
